import { useEffect, useState } from "react";
import { Brain, Check, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiAdmin";
import { HUD, timeAgo } from "./theme";

const inputCls = `h-10 rounded-xl px-3 text-sm border ${HUD.input}`;

const MemoryRow = ({ m, cats, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(m.text);
  const [cat, setCat] = useState(m.category);
  const save = async () => {
    await apiPatch(`/admin/jarvis/memory/${m.memory_id}`, { text, category: cat });
    setEdit(false);
    onChanged();
  };
  const del = async () => {
    await apiDelete(`/admin/jarvis/memory/${m.memory_id}`);
    toast.success("Forgotten");
    onChanged();
  };
  return (
    <div className="p-4 flex items-start gap-3" data-testid={`jarvis-mem-${m.memory_id}`}>
      <span className={`${HUD.mono} text-[9px] tracking-[0.2em] px-2 py-0.5 rounded-full border border-violet-400/40 text-violet-300 uppercase shrink-0 mt-0.5`}>{m.category}</span>
      {edit ? (
        <div className="flex-1 flex flex-col sm:flex-row gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid={`jarvis-mem-edit-${m.memory_id}`} className={`${inputCls} flex-1`} />
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls}>{cats.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-200 break-words">{m.text}</p>
          <p className={`${HUD.mono} text-[10px] text-gray-500 mt-1`}>from {m.source} · {timeAgo(m.updated_at)}</p>
        </div>
      )}
      <div className="flex gap-1 shrink-0">
        {edit ? (
          <>
            <button onClick={save} data-testid={`jarvis-mem-save-${m.memory_id}`} className="p-2 rounded-full text-emerald-300 hover:bg-emerald-400/10"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEdit(false)} className="p-2 rounded-full text-gray-400 hover:bg-white/5"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <button onClick={() => setEdit(true)} data-testid={`jarvis-mem-editbtn-${m.memory_id}`} className="p-2 rounded-full text-cyan-200 hover:bg-cyan-400/10"><Pencil className="w-4 h-4" /></button>
        )}
        <button onClick={del} data-testid={`jarvis-mem-del-${m.memory_id}`} className="p-2 rounded-full text-gray-500 hover:text-red-300 hover:bg-red-400/10"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const JarvisBrain = () => {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [nt, setNt] = useState("");
  const [nc, setNc] = useState("business");
  const load = (query = q) => apiGet("/admin/jarvis/memory", query ? { q: query } : {}).then((d) => { setItems(d.items); setCats(d.categories); });
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);
  const add = async () => {
    await apiPost("/admin/jarvis/memory", { text: nt, category: nc });
    setNt("");
    toast.success("JARVIS will remember that");
    load();
  };
  return (
    <div className="space-y-4" data-testid="jarvis-brain">
      <div className={`${HUD.panel} p-5 flex items-start gap-4`}>
        <Brain className="w-8 h-8 text-violet-300 shrink-0" />
        <div>
          <p className="text-white font-['Outfit'] text-lg">The Brain · {items?.length ?? 0} memories</p>
          <p className="text-xs text-gray-400">Every chat, call and text teaches JARVIS short facts about your business. It uses them everywhere — the longer you use it, the smarter it gets. Edit or delete anything. You can also say “remember that…” in chat.</p>
        </div>
      </div>
      <div className={`${HUD.panel} p-4 grid sm:grid-cols-[1fr_160px_auto] gap-2`}>
        <input placeholder="Teach JARVIS something (e.g. We never use tire shine on motorcycles)" value={nt} onChange={(e) => setNt(e.target.value)} data-testid="jarvis-mem-new-text" className={inputCls} />
        <select value={nc} onChange={(e) => setNc(e.target.value)} data-testid="jarvis-mem-new-cat" className={inputCls}>{cats.map((c) => <option key={c}>{c}</option>)}</select>
        <button onClick={add} disabled={!nt.trim()} data-testid="jarvis-mem-add" className={`${HUD.btn} h-10 px-4 text-sm flex items-center justify-center gap-1 disabled:opacity-40`}><Plus className="w-4 h-4" /> Remember</button>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-3" />
        <input placeholder="Search memory…" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} data-testid="jarvis-mem-search" className={`${inputCls} w-full pl-9`} />
      </div>
      {!items ? <Loader2 className="w-6 h-6 animate-spin text-cyan-300" /> : (
        <div className={`${HUD.panel} divide-y divide-cyan-400/5`}>
          {items.length === 0 ? <p className="p-5 text-sm text-gray-500">Nothing yet — JARVIS will start learning from your next chat or call.</p> : items.map((m) => <MemoryRow key={m.memory_id} m={m} cats={cats} onChanged={() => load()} />)}
        </div>
      )}
    </div>
  );
};

export default JarvisBrain;
